import React, { useState, useEffect, ChangeEvent } from 'react';
import { StyleSheet } from 'react-native';
import { Customer, Order } from '../interfaces';

interface SelectCustomerPopupProps {
    onClose: () => void;
    onConfirmSelection: (selectedCustomer: Customer | null) => void;
    order: Order | undefined;
}

const SelectCustomerPopup: React.FC<SelectCustomerPopupProps> = ({ onClose, onConfirmSelection, order }) => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [searchingCustomers, setSearchingCustomers] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [firstLoad, setFirstLoad] = useState(true);

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
    };

    const filteredCustomers = customers.filter((customer) =>
        customer.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const fetchCustomers = async () => {
            setSearchingCustomers(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer/`, {
                    method: 'GET',
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();
                setCustomers(data);

                if (order?.customerEmail) {
                    const match = data.find((c: Customer) => c.email === order.customerEmail);
                    if (match) {
                        setSelectedCustomer(match);
                    }
                }
            } catch (error) {
                console.error('Error fetching customers:', error);
                setCustomers([]);
            } finally {
                setSearchingCustomers(false);
                setFirstLoad(false);
            }
        };

        fetchCustomers();
    }, [order]);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const styles = StyleSheet.create({
        popup: {
            width: windowSize.width > 900 ? windowSize.width * 0.40 : windowSize.width * 0.9,
            backgroundColor: '#fff',
            position: 'absolute',
            zIndex: 9998,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
    });

    return (
        <div style={styles.popup} className='ds-pop ds-pop--center'>
            <div className='d-flex px-3 pt-3 justify-content-end'>
                <button className='text-btn p-0 m-0 d-flex justify-content-center' onClick={onClose}>
                    <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 1L1 13M1 1L13 13" stroke="#98A2B3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            </div>

            <div className='px-4 pb-3'>
                <h5>Select Customer</h5>
                <p>Select a customer to attach to this order.</p>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search customer name or email"
                    className="form-control mb-3"
                />

                {searchingCustomers && <div>Loading...</div>}

                {!searchingCustomers && filteredCustomers.length > 0 && (
                    <div className='scrollable-container' style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {filteredCustomers.map((customer) => {
                            const isSelected = selectedCustomer?.id === customer.id;
                            return (
                                <div
                                    key={customer.id}
                                    className={`border-box gap-2 p-2 mt-2 d-flex justify-content-between`}>
                                    <div className='col'>
                                        <label style={{ fontWeight: '500', fontSize: 14, lineHeight: '14px' }} className='d-flex align-items-center'>
                                            {customer.firstName} {customer.lastName}
                                        </label>
                                        <small style={{ fontSize: 12 }}>{customer.email}</small>
                                    </div>
                                    <button className={isSelected ? 'gray-filled-btn' : 'blue-filled-btn'} onClick={() => setSelectedCustomer(customer)}>
                                        {isSelected ? 'Selected' : 'Select'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!searchingCustomers && filteredCustomers.length === 0 && !firstLoad && (
                    <div className='text-center py-3'>No customers found</div>
                )}
            </div>

            <div className='p-2 d-flex justify-content-end' style={{ backgroundColor: '#F3F4F6' }}>
                <button
                    className={selectedCustomer ? 'white-filled-btn' : 'gray-filled-btn'}
                    style={{ cursor: selectedCustomer ? "pointer" : "default" }}
                    onClick={() => selectedCustomer && onConfirmSelection(selectedCustomer)}>
                    Confirm
                </button>
            </div>

            {firstLoad && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectCustomerPopup;
